import { useMemo } from 'react';

import { Alert } from '@mui/material';
import {
  CameraIcon,
  FileIcon,
  FileTextIcon,
  HouseIcon,
  MapPinIcon,
  PaperclipIcon,
  Share2Icon,
} from 'lucide-react';

import { Doc_Template_Type_Enum_Enum } from '@graphql/__generated__/graphql';
import ReportEditor from '@modules/_shared/components/report-editor/ReportEditor';
import type { StepType } from '@modules/_shared/components/report-editor/ReportEditorContext';
import type { ListingBrochureType } from '@modules/reports/brochure-report/BrochureReportEditor/BrochureReportEditor';
import { BROCHURE_REPORT_INCLUDE_FRAGMENT } from '@modules/reports/brochure-report/brochureReportsQueries';
import StepAppendFilesBrochure from '@modules/reports/brochure-report/StepAppendFilesBrochure';
import StepCoverPhotoBrochure from '@modules/reports/brochure-report/StepCoverPhotoBrochure';
import StepDescriptionBrochure from '@modules/reports/brochure-report/StepDescriptionBrochure';
import StepPhotosBrochure from '@modules/reports/brochure-report/StepPhotosBrochure';
import StepPOIBrochure from '@modules/reports/brochure-report/StepPOIBrochure';
import StepPropertyDetailsBrochure from '@modules/reports/brochure-report/StepPropertyDetailsBrochure';
import { getBrokerLanguage } from '@modules/reports/shared/language';
import StepShareReport from '@modules/reports/shared/StepShareReport';
import {
  type ListingBrochurePageId,
  extractFieldsFromFragment,
} from '@modules/reports/shared/utils';
import { useLocale } from '@shared/hooks/locale';
import type { Language } from '@shared/LocaleContext';
import { toGlobalId } from '@shared/utils/global-id';

const includeFields = extractFieldsFromFragment(
  BROCHURE_REPORT_INCLUDE_FRAGMENT,
);

type ListingBrochureReportEditorWorkflowProps = {
  listingBrochure: ListingBrochureType;
  previewOnly?: boolean;
  readOnly?: boolean;
  language?: Language;
};

const activeStepIconProps = { size: 20, strokeWidth: 2.75 };

const BrochureReportEditorWorkflow = ({
  listingBrochure,
  previewOnly = false,
  readOnly = false,
  language,
}: ListingBrochureReportEditorWorkflowProps) => {
  const { t } = useLocale();
  const documentId = toGlobalId('Lot', listingBrochure.id);

  const steps = useMemo<StepType<ListingBrochurePageId>[]>(
    () => [
      {
        title: t('Cover'),
        Component: StepCoverPhotoBrochure,
        pageId: 'page-cover',
        icon: <FileIcon size={20} />,
        activeIcon: <FileIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_cover,
        databaseToggleField: 'brochure_include_cover',
        position: 'main',
      },
      {
        title: t('Description'),
        Component: StepDescriptionBrochure,
        pageId: 'page-description',
        icon: <FileTextIcon size={20} />,
        activeIcon: <FileTextIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_description,
        databaseToggleField: 'brochure_include_description',
        position: 'main',
      },
      {
        title: t('Property'),
        Component: StepPropertyDetailsBrochure,
        pageId: 'page-property',
        icon: <HouseIcon size={20} />,
        activeIcon: <HouseIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_property,
        databaseToggleField: 'brochure_include_property',
        position: 'main',
      },
      {
        title: t('Points of Interest'),
        Component: StepPOIBrochure,
        pageId: 'page-point-of-interest',
        icon: <MapPinIcon size={20} />,
        activeIcon: <MapPinIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_pois,
        databaseToggleField: 'brochure_include_pois',
        position: 'main',
      },
      {
        title: t('Photos'),
        Component: StepPhotosBrochure,
        pageId: 'page-photos',
        icon: <CameraIcon size={20} />,
        activeIcon: <CameraIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_photos,
        databaseToggleField: 'brochure_include_photos',
        position: 'main',
      },
      {
        title: t('Append files'),
        Component: StepAppendFilesBrochure,
        pageId: 'page-append-files',
        icon: <PaperclipIcon size={20} />,
        activeIcon: <PaperclipIcon {...activeStepIconProps} />,
        active: listingBrochure.brochure_include_append_files,
        databaseToggleField: 'brochure_include_append_files',
        position: 'main',
      },
      {
        title: t('Share report'),
        Component: StepShareReport,
        pageId: 'page-share',
        icon: <Share2Icon size={20} />,
        activeIcon: <Share2Icon {...activeStepIconProps} />,
        position: 'footer',
      },
    ],
    [
      listingBrochure.brochure_include_cover,
      listingBrochure.brochure_include_description,
      listingBrochure.brochure_include_photos,
      listingBrochure.brochure_include_property,
      listingBrochure.brochure_include_pois,
      listingBrochure.brochure_include_append_files,
      t,
    ],
  );

  if (listingBrochure.doc_template == null) {
    return (
      <Alert severity="warning">{t('No brochure template available')}</Alert>
    );
  }

  return (
    <ReportEditor<ListingBrochurePageId>
      steps={steps}
      data={listingBrochure}
      documentId={documentId}
      templateId={listingBrochure.doc_template.id}
      documentType={Doc_Template_Type_Enum_Enum.LotBrochure}
      language={getBrokerLanguage(listingBrochure.broker, language)}
      exitPath={`/listings/${listingBrochure.id}`}
      includeFields={includeFields}
      previewOnly={previewOnly}
      readOnly={readOnly}
    />
  );
};

export default BrochureReportEditorWorkflow;
